import ArrowDownTrayIcon from "@heroicons/react/24/outline/ArrowDownTrayIcon";
import ArrowUpOnSquare from "@heroicons/react/24/outline/ArrowUpOnSquareIcon";
import BellIcon from "@heroicons/react/24/outline/BellIcon";
import BellSlashIcon from "@heroicons/react/24/outline/BellSlashIcon";
import $_ from "classnames";
import { useEffect, useState } from "react";
import { Button, Tooltip } from "react-daisyui";
import { execQuery, mutations, queries, types, wrapMutation } from "../graphql";
import { useAddToHomescreenPrompt } from "../hooks/useAddToHomescreenPrompt";

type NOTIFICATION_STATUS = true | false | "UNDETERMINED" | "UNSUPPORTED";

const VAPID_PUBLIC_KEY =
  "BHgMHd99XJaawyaptfP8Omb8_I0xe-_CYE2l5RkggOsiCFKI4kqzzvZLEazNSvwv-6OjpGF_0EByDJROzy1a1vk";

function NotificationsIcon({
  currentUser,
  zone,
}: {
  currentUser: types.User | null;
  zone: types.Zone | null;
}) {
  const [prompt, promptToInstall] = useAddToHomescreenPrompt();
  const [notificationsEnabled, setNotificationsEnabled] =
    useState<NOTIFICATION_STATUS>("UNDETERMINED");
  const [appIsInstallable, setAppIsInstallable] = useState(false);

  function urlBase64ToUint8Array(base64String: string) {
    var padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    var base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

    var rawData = window.atob(base64);
    var outputArray = new Uint8Array(rawData.length);

    for (var i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  useEffect(() => {
    if (prompt) {
      setAppIsInstallable(true);
    }
  }, [prompt]);

  useEffect(() => {
    (async () => {
      if (zone?.zoneId && currentUser) {
        if (!("serviceWorker" in navigator && "PushManager" in window)) {
          setNotificationsEnabled("UNSUPPORTED");
        } else {
          navigator.serviceWorker.register("service-worker.js");
          navigator.serviceWorker.ready.then(async (registration) => {
            const subscription =
              await registration.pushManager.getSubscription();
            if (!subscription) {
              setNotificationsEnabled(false);
            } else {
              return execQuery(queries.isPushSubscriptionRegistered, {
                zoneId: zone.zoneId,
                pushSubscription: JSON.stringify(subscription),
              }).then(
                (response) => {
                  console.log("pushSubscriptionsResponse:", response);
                  setNotificationsEnabled(
                    !!response.data?.isPushSubscriptionRegistered,
                  );
                },
                (err) => {
                  if (err.errors[0].errorType === "ParticipantNotFound") {
                    setNotificationsEnabled(false);
                  }
                },
              );
            }
          });
        }
      }
    })();
  }, [zone?.zoneId, currentUser]);

  const modifySubscription = async (enableNotifications: boolean) => {
    if (zone?.zoneId) {
      navigator.serviceWorker.register("service-worker.js");
      await navigator.serviceWorker.ready.then((registration) => {
        return registration.pushManager
          .getSubscription()
          .then(async (subscription) => {
            if (subscription) {
              if (enableNotifications) {
                return wrapMutation(mutations.addPushSubscription)({
                  zoneId: zone.zoneId,
                  pushSubscription: JSON.stringify(subscription),
                }).then((result) => {
                  setNotificationsEnabled(true);
                });
              } else {
                return wrapMutation(mutations.removePushSubscription)({
                  zoneId: zone.zoneId,
                  pushSubscription: JSON.stringify(subscription),
                }).then((result) => {
                  setNotificationsEnabled(false);
                });
              }
            } else {
              if (!enableNotifications) {
                return;
              } else {
                return registration.pushManager
                  .subscribe({
                    userVisibleOnly: true,
                    applicationServerKey:
                      urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
                  })
                  .then((subscription) => {
                    if (subscription) {
                      return wrapMutation(mutations.addPushSubscription)({
                        zoneId: zone.zoneId,
                        pushSubscription: JSON.stringify(subscription),
                      }).then((result) => {
                        setNotificationsEnabled(true);
                      });
                    } else {
                    }
                  })
                  .catch((err) => {
                    console.log("Error", err);
                  });
              }
            }
          });
      });
    }
  };

  switch (notificationsEnabled) {
    case "UNDETERMINED":
      return (
        <Button className="btn-ghost  h-auto min-h-fit w-auto min-w-fit items-center  justify-center rounded-md p-2 transition duration-1000 ease-in-out">
          <BellSlashIcon className="h-6 w-6 " />
        </Button>
      );
    case "UNSUPPORTED":
      return (
        <Tooltip message="Install Bandwagon" className="tooltip-left">
          <button
            onClick={() => {
              if (appIsInstallable) {
                promptToInstall();
              } else {
                alert("Show help");
                // window.ShowInstallHelp.showModal();
              }
            }}
            className={$_(
              "btn h-auto min-h-fit w-auto min-w-fit items-center justify-center rounded-md p-2 transition duration-500 ease-in-out",
              appIsInstallable ? "btn-accent" : "btn-neutral",
            )}
          >
            <ArrowDownTrayIcon className="h-6 w-6" />
          </button>
          <dialog id="ShowInstallHelp" className="modal">
            <form method="dialog" className="modal-box">
              <h3 className="text-lg font-bold">Install Bandwagon</h3>
              <p className="py-4">
                Use the <ArrowUpOnSquare className="inline" /> icon in your
                browser menu and select "Add to Home Screen" to Install
                Bandwagon and unlock notifications.
              </p>
            </form>
            <form method="dialog" className="modal-backdrop">
              <button>close</button>
            </form>
          </dialog>
        </Tooltip>
      );
    case true:
      return (
        <Tooltip message="Notifications Enabled" className="tooltip-left">
          <Button
            onClick={() => modifySubscription(false)}
            className="btn-ghost h-auto min-h-fit w-auto min-w-fit items-center justify-center rounded-md text-primary transition duration-500 ease-in-out"
          >
            <BellIcon className="h-6 w-6" />
          </Button>
        </Tooltip>
      );
    case false:
      return (
        <Tooltip message="Enable Notifications" className="tooltip-left">
          <Button
            onClick={() => modifySubscription(true)}
            className="btn-ghost h-auto min-h-fit w-auto min-w-fit items-center justify-center rounded-md p-2 text-secondary transition duration-500 ease-in-out"
          >
            <BellSlashIcon className={"h-6 w-6 fill-current"} />
          </Button>
        </Tooltip>
      );
  }
}

export default NotificationsIcon;
